import { message } from 'antd'
import {
  RugFormDataState,
  RugReportSectionId,
} from 'pages/reporting/reports/rug/rugReportData'
import { ComponentId } from 'pages/reporting/reports/rug/types'
import { getRequiredSections, Report } from 'pages/reporting/shared/types'
import { useCallback, useMemo, useState } from 'react'
import { GeckoComponent, Sectionable } from 'types'

import { AssetScreenshots } from './useAssetScreenshots'

interface SectionStatus {
  isComplete: boolean
  completedFields: number
  totalFields: number
}

/**
 * Check if a value is valid (non-null, non-empty, or valid nested structure)
 */
const isValidValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false
  }

  if (typeof value === 'string' && value === '') {
    return false
  }

  if (Array.isArray(value)) {
    const filtered = value.filter(
      (item) => item !== null && item !== '' && item !== undefined
    )
    return filtered.length > 0
  }

  if (typeof value === 'object') {
    const keys = Object.keys(value)
    if (keys.length === 0) {
      return false
    }
    return keys.every((key) =>
      isValidValue((value as Record<string, unknown>)[key])
    )
  }

  return true
}

/**
 * Calculate completion status for a section based on required fields
 */
const calculateSectionCompletion = (
  requiredFields: string[],
  data: Record<string, unknown> | undefined
): SectionStatus => {
  if (!data) {
    return {
      isComplete: false,
      completedFields: 0,
      totalFields: requiredFields.length,
    }
  }
  let completedFields = 0
  const totalFields = requiredFields.length

  requiredFields.forEach((field) => {
    const value = data[field]
    if (isValidValue(value)) {
      completedFields++
    }
  })

  return {
    isComplete: completedFields > 0 && completedFields === totalFields,
    completedFields,
    totalFields,
  }
}

/**
 * Get completion status for RUG report sections
 */
const getRugReportCompletionStatus = (
  formData: RugFormDataState,
  assetScreenshots: AssetScreenshots | undefined,
  selectedComponents: readonly GeckoComponent[] | undefined,
  sectionsByComponent: Map<ComponentId, Sectionable>
): Record<string, SectionStatus> => {
  const status: Record<string, SectionStatus> = {}

  const fallbackStatus: SectionStatus = {
    isComplete: false,
    completedFields: 0,
    totalFields: 0,
  }

  // Cover section (required)
  const coverFields = ['inspectionStartDate', 'inspectionEndDate']
  status.cover = calculateSectionCompletion(coverFields, {
    inspectionStartDate: formData.coverSectionData?.inspectionStartDate,
    inspectionEndDate: formData.coverSectionData?.inspectionEndDate,
  })

  // Summary section (required)
  const summaryFields = [
    'inspectionResolution',
    'deliverableResolution',
    'coverageScope',
    'corrosionBLUF',
  ]
  status.summary = calculateSectionCompletion(summaryFields, {
    inspectionResolution: formData.summarySectionData?.inspectionResolution,
    deliverableResolution: formData.summarySectionData?.deliverableResolution,
    coverageScope: formData.summarySectionData?.coverageScope,
    corrosionBLUF: formData.summarySectionData?.corrosionBLUF,
  })

  // Data Sheet section (required)
  const dataSheetFields = [
    'sensorSerialNumbers',
    'inspectionSurface',
    'surfaceCondition',
    'coatingPresent',
    'robots',
    'calibrationBlocks',
    'calibrationSteps',
    'payloads',
  ]
  const dataSheetData = {
    sensorSerialNumbers: formData.dataSheetSectionData?.sensorSerialNumbers,
    inspectionSurface: formData.dataSheetSectionData?.inspectionSurface,
    surfaceCondition: formData.dataSheetSectionData?.surfaceCondition,
    coatingPresent: formData.dataSheetSectionData?.coatingPresent,
    robots: formData.dataSheetSectionData?.robots,
    calibrationBlocks: formData.dataSheetSectionData?.calibrationBlocks,
    calibrationSteps: formData.dataSheetSectionData?.calibrationSteps,
    payloads: formData.dataSheetSectionData?.payloads,
  }

  status[RugReportSectionId.DATA_SHEET] = calculateSectionCompletion(
    dataSheetFields,
    dataSheetData
  )

  // Images section (optional)
  if (formData.imagesSectionData?.imageReportIds !== undefined) {
    const hasImages = formData.imagesSectionData.imageReportIds.length > 0
    status[RugReportSectionId.IMAGES] = {
      isComplete: hasImages,
      completedFields: hasImages ? 1 : 0,
      totalFields: 1,
    }
  } else {
    status[RugReportSectionId.IMAGES] = {
      ...fallbackStatus,
    }
  }

  // Data Overview section
  const componentCount = selectedComponents?.length ?? 0
  const screenshotCount = assetScreenshots?.fullComponentHeatmap?.length ?? 0
  status[RugReportSectionId.DATA_OVERVIEW] = {
    ...fallbackStatus,
    isComplete: screenshotCount === componentCount,
    completedFields: screenshotCount,
    totalFields: componentCount,
  }

  // Rug Data Images section
  const componentsWithSectionsCount = sectionsByComponent.size
  const completedComponentCount =
    selectedComponents
      ?.map((component) => {
        const sectionable = sectionsByComponent.get(component.id as ComponentId)
        if (!sectionable) return false
        const sectionScreenshots = assetScreenshots?.sectionHeatmap?.filter(
          (screenshot) => screenshot.componentId === component.id
        )
        return (
          sectionScreenshots &&
          sectionable.sections.length === sectionScreenshots.length
        )
      })
      .reduce((acc, curr) => acc + (curr ? 1 : 0), 0) ?? 0
  status[RugReportSectionId.RUG_DATA_IMAGES] = {
    isComplete: completedComponentCount === componentsWithSectionsCount,
    completedFields: completedComponentCount,
    totalFields: componentsWithSectionsCount,
  }

  // Review section (checking signoff)
  const supervisorSigned = !!formData.signoff?.supervisorSignoff
  const reviewerSigned = !!formData.signoff?.reviewerSignoff
  const signoffCount = Number(supervisorSigned) + Number(reviewerSigned)
  status[RugReportSectionId.REVIEW] = {
    isComplete: supervisorSigned && reviewerSigned,
    completedFields: signoffCount,
    totalFields: 2,
  }

  // PDF Generation section (always complete)
  status[RugReportSectionId.PDF_GENERATION] = {
    isComplete: true,
    completedFields: 1,
    totalFields: 1,
  }

  return status
}

interface UseReportNavigationResult {
  activePage: string | null
  navigateToPage: (page: string) => void
  returnToMenu: () => void
  canNavigate: (page: string) => boolean
  completionStatus: Record<
    string,
    {
      isComplete: boolean
      completedFields: number
      totalFields: number
    }
  >
}

interface UseReportNavigationProps {
  formData: RugFormDataState
  report: Report
  assetScreenshots: AssetScreenshots | undefined
  selectedComponents: readonly GeckoComponent[] | undefined
  sectionsByComponent: Map<ComponentId, Sectionable>
}

/**
 * Hook to manage report navigation and page completion status for RUG reports
 */
export const useReportNavigation = ({
  formData,
  report,
  assetScreenshots,
  selectedComponents,
  sectionsByComponent,
}: UseReportNavigationProps): UseReportNavigationResult => {
  const [activePage, setActivePage] = useState<string | null>(null)

  // Calculate completion status for all sections
  const completionStatus = useMemo(
    () =>
      getRugReportCompletionStatus(
        formData,
        assetScreenshots,
        selectedComponents,
        sectionsByComponent
      ),
    [formData, assetScreenshots, selectedComponents, sectionsByComponent]
  )

  /**
   * Check if navigation to a page is allowed based on dependencies
   */
  const canNavigate = useCallback(
    (page: string): boolean => {
      // For review page, check if all required sections are complete
      if (page === 'review') {
        const requiredSections: string[] = getRequiredSections(report)
        const hasIncomplete = requiredSections.some(
          (section) => !completionStatus[section]?.isComplete
        )

        if (hasIncomplete) {
          message.warning('Please complete all required sections before review')
          // Commented out for development purposes
          // return false
        }
      }

      // For PDF generation, check if review is complete
      if (page === 'pdfGeneration' && !completionStatus.review?.isComplete) {
        message.warning(
          'Please complete the review section before generating PDF'
        )
        // Commented out for development purposes
        // return false
      }

      return true
    },
    [completionStatus, report]
  )

  /**
   * Navigate to a specific page
   */
  const navigateToPage = useCallback(
    (page: string) => {
      if (canNavigate(page)) {
        setActivePage(page)
      }
    },
    [canNavigate]
  )

  /**
   * Return to the main menu
   */
  const returnToMenu = useCallback(() => {
    setActivePage(null)
  }, [])

  return {
    activePage,
    navigateToPage,
    returnToMenu,
    canNavigate,
    completionStatus,
  }
}
